import { isApiError } from '@/api/errors';

export type CreateMailboxField = 'localPart' | 'domainId' | 'password';

/** Where to show a create failure: next to a field, or as a banner over the form. */
export interface CreateMailboxProblem {
  field?: CreateMailboxField;
  message: string;
}

/**
 * Turn a create failure into a specific, actionable message. Branches on the
 * server's stable `code` (kept even when the transport message is replaced by a
 * generic one, as it is for every 404), then on the error kind.
 */
export function createMailboxError(e: unknown, address: string, domain: string): CreateMailboxProblem {
  if (!isApiError(e)) return { message: 'The mailbox could not be created. Please try again.' };
  switch (e.code) {
    case 'DOMAIN_NOT_MAIL_ENABLED':
    case 'DOMAIN_NOT_VERIFIED':
      return { field: 'domainId', message: `${domain} is not verified for email yet. Finish verifying the domain, then try again.` };
    case 'DOMAIN_NOT_PROVISIONED':
      return { field: 'domainId', message: `${domain} is verified but not yet enabled for mailboxes. Please contact support.` };
    case 'DOMAIN_NOT_IN_TENANT':
    case 'DOMAIN_NOT_FOUND':
      return { field: 'domainId', message: 'That domain is not part of this organization. Choose one of your domains.' };
    case 'ADDRESS_EXISTS':
      return { field: 'localPart', message: `${address} already exists.` };
    case 'INVALID_LOCAL_PART':
    case 'INVALID_ADDRESS':
      return { field: 'localPart', message: 'Use letters, numbers, dots, hyphens, underscores or plus signs, starting and ending with a letter or number.' };
    case 'PASSWORD_POLICY':
      return { field: 'password', message: e.message };
    case 'QUOTA_EXCEEDED':
      return { message: 'There is not enough storage left in your plan for this quota. Lower the quota or upgrade your plan.' };
    case 'PLAN_LIMIT_REACHED':
      return { message: 'Your plan’s mailbox limit has been reached. Remove a mailbox or upgrade your plan.' };
    case 'DOWNSTREAM_UNAVAILABLE':
    case 'SERVICE_UNAVAILABLE':
    case 'ENTITLEMENTS_UNAVAILABLE':
      return { message: 'The mail service is temporarily unavailable. Nothing was created; please try again shortly.' };
  }
  switch (e.kind) {
    case 'downstream_unavailable':
    case 'network':
      return { message: 'The mail service is temporarily unavailable. Nothing was created; please try again shortly.' };
    case 'forbidden':
      return { message: 'You do not have permission to create mailboxes.' };
    case 'validation':
      return { message: e.message };
    default:
      return { message: 'The mailbox could not be created. Please try again.' };
  }
}
